import React, { useContext, useEffect, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Checkbox, DataTable } from "react-native-paper";
import { AntDesign, Feather, FontAwesome } from "@expo/vector-icons";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { useIsFocused } from "@react-navigation/native";
import UpdateConfirmationScreen from "./partials/UpdateConfirmationScreen";
import { useToast } from "react-native-toast-notifications";
import { useSelector } from "react-redux";
import {
  deleteOperationById,
  manageOperation,
  retrieveHubspotAttachmentsById,
} from "../../services/operationService";
import { processAttachments } from "../../services/s3BucketService";
import FilePreview from "./utils/FilePreview";
import OrderActionComponent from "./partials/OrderActionComponent ";
import Spinner from "../../components/shared/Spinner";
import moment from "moment";
import OperationHistoryModal from "./partials/OperationHistoryModal";

interface Props {
  navigation: any;
  route: {
    params: {
      action: string;
    };
  };
}

const SingleOperation: React.FC<Props> = ({ navigation, route }) => {
  const toast = useToast();
  const { isMediumDevice, isMobileDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const { currentRole } = useSelector((state: any) => state.configurations);

  // Responsive layout helper
  const isLargeScreen = !isMobileDevice && !isSmallDevice && !isMediumDevice;

  const isFocused = useIsFocused();
  const [title, setTitle] = useState("");
  const [action, setAction] = useState("");
  const [confirmationTextCS, setConfirmationTextCS] = useState("");
  const [successfulTextCS, setSuccessfulTextCS] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [approved, setApproved] = useState(false);
  const { shippingMethodList, isAttachmentsLoaded, operationAttachmentsList } =
    useSelector((state: any) => state.operations);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalPieces, setTotalPieces] = useState(0);
  const [totalBoxes, setTotalBoxes] = useState(0);
  const { stockReportList } = useSelector((state: any) => state.reports);
  let { order } = route.params;
  const [selectedOrder, setSelectedOrder] = useState(order || null);
  const shippingMethodItem = shippingMethodList.find(
    (sm: any) => sm.value == order.shippingMethod
  );

  const [sapDocNumber, setSapDocNumber] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [showModal, setShowModal] = useState<boolean>(false);
  const onClose = () => {
    setShowModal(false);
  };

  // Collapsible card states - initialized as collapsed (true)
  const [collapsedCards, setCollapsedCards] = useState({
    customer: true,
    order: true,
    shipping: true,
    vendor: true,
    products: true,
    files: true,
    freight: true,
    submission: true,
  });

  const toggleCard = (cardName: keyof typeof collapsedCards) => {
    setCollapsedCards((prev) => ({
      ...prev,
      [cardName]: !prev[cardName],
    }));
  };

  // Auto-expand cards that have data
  useEffect(() => {
    if (selectedOrder) {
      const hasCustomerData =
        selectedOrder.customerName || selectedOrder.customerEmail;
      const hasOrderData =
        selectedOrder.deliveryDate ||
        selectedOrder.orderStatus ||
        selectedOrder.containerSize;
      const hasShippingData =
        selectedOrder.address || selectedOrder.shippingMethod;
      const hasVendorData =
        selectedOrder.new_line_item_ ||
        selectedOrder.product_details ||
        selectedOrder.labelRequirements ||
        selectedOrder.artworkApproved ||
        selectedOrder.artwork_ref___label_1_ ||
        selectedOrder.artwork_ref___label_2_ ||
        selectedOrder.new_product___artwork___sampling___label_flags;
      const hasProductsData =
        selectedOrder.products && selectedOrder.products.length > 0;
      const hasFilesData =
        (operationAttachmentsList && operationAttachmentsList.length > 0) ||
        (selectedOrder.attachments && selectedOrder.attachments.length > 0);
      const hasFreightData = selectedOrder.freightDelivery;
      const hasSubmissionData =
        selectedOrder.createdAt || selectedOrder.updatedAt;

      setCollapsedCards({
        customer: !hasCustomerData,
        order: !hasOrderData,
        shipping: !hasShippingData,
        vendor: !hasVendorData,
        products: !hasProductsData,
        files: !hasFilesData,
        freight: !hasFreightData,
        submission: !hasSubmissionData,
      });
    }
  }, [selectedOrder, operationAttachmentsList]);

  useEffect(() => {
    if (order._id == undefined) {
      setTimeout(() => {
        navigation.navigate("OperationsManagement");
      }, 1500);
    } else {
      setSelectedOrder(order);
      initializeProducts(order);
      calculateTotals(order);

      // fetch hubspot attachments
      if (order.hubspotId) {
        retrieveHubspotAttachmentsById(order.hubspotId as string);
      }
    }
  }, [order]);

  useEffect(() => {
    if (isFocused) {
      switch (route.params.action) {
        case "edit":
          setTitle("Update Order");
          setApproved(true);
          updateOperation();
          break;
        case "view":
          setTitle("View Order");
          setApproved(true);
          break;
        case "requestHelp":
          setTitle("Request For Help");
          setApproved(false);
          break;
        case "delete":
          setTitle("Delete Order");
          setApproved(false);
          break;
        case "clone":
          setTitle("Clone Order");
          setApproved(false);
          break;
        default:
          break;
      }
      setAction(route.params.action);
      setLoaded(true);
    } else setLoaded(false);
  }, [isFocused]);

  useEffect(() => {
    switch (action) {
      case "edit":
        setTitle("Update Order");
        updateOperation();
        break;
      case "requestHelp":
        setTitle("Request For Help");
        setConfirmationTextCS("Are you sure you want to process the order?");
        break;
      case "delete":
        setTitle("Cancelled Order");
        setConfirmationTextCS("Request to cancel the Order");
        break;
      case "clone":
        setTitle("Clone Order");
        setConfirmationTextCS("Are you sure you want to clone this order?");
        break;
      default:
        break;
    }
    setSuccessfulTextCS("Request Submitted");
  }, [action]);

  const cancelOperation = async () => {
    const updatedOrder = {
      ...order,
      orderStatus: "Cancelled",
    };

    await manageOperation(updatedOrder, "edit");
  };

  // Process operation based on order status
  const processOperation = async (sapDoc: string) => {
    const updatedOrder = {
      ...selectedOrder,
      orderStatus:
        selectedOrder.orderStatus === "Pending" ? "Processed" : "Completed",
      ...(selectedOrder.orderStatus === "Completed"
        ? { isApproved: true }
        : { sapDocumentNumber: sapDoc || selectedOrder.sapDocumentNumber }),
    };

    await manageOperation(updatedOrder, "edit");
  };

  // Handle input for SAP Document Number and process the order
  const handleProcessOrder = async () => {
    if (selectedOrder.orderStatus === "Pending" && !sapDocNumber) {
      toast.show("Please provide the SAP Document Number to proceed", {
        type: "warning",
      });
      return;
    }
    setLoading(true);
    await processOperation(sapDocNumber);
    setLoading(false);
    toast.show(
      selectedOrder.orderStatus === "pending"
        ? "The order has been processed and requested for approval completion"
        : "The order has been marked as completed",
      { type: "success" }
    );
    navigation.navigate("OperationsManagement");
  };

  useEffect(() => {
    if (approved) {
      switch (action) {
        case "requestHelp":
          handleProcessOrder();
          break;
        case "delete":
          cancelOperation();
          toast.show("Request for cancellation has been sent", {
            type: "danger",
          });
          break;
        case "clone":
          cloneOperation();
        default:
          break;
      }
    }
  }, [approved]);

  const approveCS = () => {
    setApproved(true);
  };

  const rejectCS = () => {
    navigation.navigate("OperationsManagement");
  };

  const updateOperation = () => {
    navigation.navigate("PlaceOrder", {
      operation: order,
      titleToDisplay: "Update Order",
      requestType: "place",
      mode: "edit",
    });
  };

  const cloneOperation = () => {
    navigation.navigate("PlaceOrder", {
      operation: order,
      titleToDisplay: "Update Order",
      requestType: "place",
      mode: "clone",
    });
  };

  const mobileDevice = isSmallDevice || isMediumDevice || isMobileDevice;

  const contentStyle = {
    width: mobileDevice ? "100%" : "60%",
    maxWidth: mobileDevice ? "100%" : "60%",
    flex: 1,
    borderWidth: !mobileDevice ? 1 : 0,
    borderColor: "#efefef",
  };

  const formatDate = (isoString: string) => {
    return moment.utc(isoString).format("DD / MM / YYYY");
  };

  const deleteOperation = async (id: string) => {
    if (confirm("Do you want to Delete this Operation?")) {
      await deleteOperationById(id)
        .then((result) => {
          toast.show(result.data.message, {
            type: "success",
          });

          navigation.navigate("OperationsManagement");
          // backToList();
        })
        .catch((err) => {
          toast.show(err.message, {
            type: "danger",
          });
        })
        .finally(() => {
          // setDisplayLoader(!displayLoader);
        });
    }
  };

  const updateCurrentAction = (action: string) => {
    setAction((prev) => action);
  };

  const initializeProducts = (selectedOrder: any) => {
    if (selectedOrder && selectedOrder?.products?.length > 0) {
      const newProductsList = selectedOrder.products.map((product: any) => {
        // Find matching productValue
        const matchingProductValue = stockReportList.find((item: any) => {
          const itemItemCode = ["ADMIN", "OWNER"].includes(
            currentRole.toUpperCase()
          )
            ? item.ItemCode[0]
            : item.ItemCode;

          return (
            itemItemCode.toLowerCase() === product.ItemCode.toLowerCase() &&
            item.Location.toLowerCase() === product.Location.toLowerCase()
          );
        });

        const productPrice = product.price ? parseFloat(product.price) : 1;
        const productQuantity = product.quantity
          ? parseFloat(product.quantity)
          : 0;

        // Merge product with the matching productValue, if it exists
        return {
          ...product,
          ...matchingProductValue, // Add properties from productValue
          totalPrice: productPrice * productQuantity,
          totalBoxes: product.uom === "Boxes" ? productQuantity : 0,
          totalPieces:
            product.uom === "Boxes"
              ? productQuantity * product.PerCtns
              : productQuantity,
        };
      });

      // Update selectedOrder with the new products list
      setSelectedOrder((prev: any) => ({
        ...prev, // Keep other properties of the selectedOrder
        products: newProductsList, // Update the products array
      }));
    }

    const handleAttachments = async (operation: any) => {
      // Await the result of processAttachments to get the resolved attachments array
      const attachments = await processAttachments(operation.attachments ?? []);

      // Add custom fields like fileName, fileUri, and fileType to each attachment
      const updatedAttachments = attachments.map((attachment) => {
        return {
          ...attachment,
          fileName: attachment?.fileName, // Custom field
          fileUri: attachment?.fileUri, // Custom field
          fileType: attachment?.fileType, // Custom field
        };
      });

      // Now set the value with the updated attachments that include custom fields
      setSelectedOrder((prev: any) => ({
        ...prev, // Keep other properties of the selectedOrder
        attachments: updatedAttachments, // Update the products array
      }));
    };

    // Call the function in your code
    handleAttachments(selectedOrder);
  };

  const calculateTotals = (selectedOrder: any) => {
    let totalPrice = 0;
    let totalBoxes = 0;
    let totalPieces = 0;

    // Iterate through products and calculate totals
    selectedOrder.products.forEach((product: any) => {
      totalPrice += product.price * product.quantity;

      // Check if the product has a UoM and count accordingly
      if (product.uom === "Boxes") {
        totalBoxes += product.quantity;
      } else if (product.uom === "Pieces") {
        totalPieces += product.quantity;
      }
    });
    setTotalPrice(totalPrice);
    setTotalBoxes(totalBoxes);
    setTotalPieces(totalPieces);
  };
  const DrawerHeader = () => {
    return (
      <View style={styles.drawerStyle}>
        <View style={{ flex: 1, flexDirection: "row" }}>
          <View style={{ paddingHorizontal: 15, paddingVertical: 5 }}>
            <TouchableOpacity onPress={() => navigation.goBack()}>
              <AntDesign name="arrowleft" size={24} color="black" />
            </TouchableOpacity>
          </View>
          <View style={{ paddingLeft: 10, justifyContent: "center" }}>
            <Text style={{ fontWeight: "500" }}>
              {title} - {selectedOrder.documentNumber}
            </Text>
          </View>
        </View>
        {action == "view" &&
          selectedOrder?.orderStatus != "Completed" &&
          selectedOrder?.orderStatus != "Completed" && (
            <View style={{ flexDirection: "row", gap: 2 }}>
              <TouchableOpacity
                onPress={() => {
                  setShowModal(true);
                }}
                style={styles.editButton}
              >
                <View style={styles.headerBtnContainer}>
                  <Feather name="rewind" size={18} color="green" />
                </View>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={updateOperation}
                style={styles.editButton}
              >
                <View style={styles.headerBtnContainer}>
                  <Feather name="edit-2" size={18} color="#007bff" />
                </View>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => deleteOperation(selectedOrder._id)}
                style={styles.editButton}
              >
                <View style={styles.headerBtnContainer}>
                  <Feather name="trash" size={18} color="red" />
                </View>
              </TouchableOpacity>
            </View>
          )}
      </View>
    );
  };

  return (
    <>
      {showModal && order && (
        <OperationHistoryModal
          onClose={onClose}
          visible={showModal}
          order={order}
        />
      )}

      <View
        style={{ alignItems: "center", flex: 1, backgroundColor: "#f8f9fa" }}
      >
        {loading || !isAttachmentsLoaded ? (
          <View style={styles.loadingContainer}>
            <Spinner size="large" color="#007bff" />
          </View>
        ) : (
          <View style={contentStyle}>
            {!loaded ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator
                  size="large"
                  color="#007bff"
                  style={{ marginVertical: 5 }}
                />
              </View>
            ) : (
              <>
                {approved ? (
                  <>
                    <DrawerHeader />
                    <ScrollView style={styles.container}>
                      {/* Customer Details Card */}
                      <View style={styles.card}>
                        <TouchableOpacity
                          style={styles.cardHeader}
                          onPress={() => toggleCard("customer")}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.cardTitle}>Customer Details</Text>
                          <AntDesign
                            name={collapsedCards.customer ? "down" : "up"}
                            size={20}
                            color="#495057"
                          />
                        </TouchableOpacity>
                        {!collapsedCards.customer && (
                          <View style={styles.cardContent}>
                            <View style={styles.infoGrid}>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Customer Name
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.customerName}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Customer Ref#
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.customerReference}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Contact</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.customerNumber}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Email</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.customerEmail}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Payment Terms
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.paymentTerms}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Owner</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.ownerName}
                                </Text>
                              </View>
                            </View>
                          </View>
                        )}
                      </View>

                      {/* Order Details Card */}
                      <View style={styles.card}>
                        <TouchableOpacity
                          style={styles.cardHeader}
                          onPress={() => toggleCard("order")}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.cardTitle}>Order Details</Text>
                          <AntDesign
                            name={collapsedCards.order ? "down" : "up"}
                            size={20}
                            color="#495057"
                          />
                        </TouchableOpacity>
                        {!collapsedCards.order && (
                          <View style={styles.cardContent}>
                            <View style={styles.infoGrid}>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Delivery Date
                                </Text>
                                <Text style={styles.infoValue}>
                                  {formatDate(selectedOrder.deliveryDate)}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Order Date</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.orderDate &&
                                    formatDate(selectedOrder.orderDate)}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Order Type</Text>
                                <Text style={[styles.infoValue, styles.badge]}>
                                  {selectedOrder?.orderType}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Order Status
                                </Text>
                                <Text
                                  style={[styles.infoValue, styles.statusBadge]}
                                >
                                  {selectedOrder?.orderStatus}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>On Hold</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.onHold || "No"}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Container Size
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.containerSize || "-"}
                                </Text>
                              </View>
                            </View>

                            {/* Additional Order Flags */}
                            <View style={styles.flagsContainer}>
                              {selectedOrder.urgentOrder &&
                                selectedOrder.urgentOrder === "true" && (
                                  <View
                                    style={[styles.flag, styles.urgentFlag]}
                                  >
                                    <Text style={styles.flagText}>
                                      Urgent Order
                                    </Text>
                                  </View>
                                )}
                              {selectedOrder.priceVariationRequested &&
                                selectedOrder.priceVariationRequested ===
                                  "true" && (
                                  <View style={[styles.flag, styles.priceFlag]}>
                                    <Text style={styles.flagText}>
                                      Price Variation Requested
                                    </Text>
                                  </View>
                                )}
                              {selectedOrder.accountApproved &&
                                selectedOrder.accountApproved === "true" && (
                                  <View
                                    style={[styles.flag, styles.approvedFlag]}
                                  >
                                    <Text style={styles.flagText}>
                                      Account Approved
                                    </Text>
                                  </View>
                                )}
                            </View>
                          </View>
                        )}
                      </View>

                      {/* Shipping Details Card */}
                      <View style={styles.card}>
                        <TouchableOpacity
                          style={styles.cardHeader}
                          onPress={() => toggleCard("shipping")}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.cardTitle}>Shipping Details</Text>
                          <AntDesign
                            name={collapsedCards.shipping ? "down" : "up"}
                            size={20}
                            color="#495057"
                          />
                        </TouchableOpacity>
                        {!collapsedCards.shipping && (
                          <View style={styles.cardContent}>
                            <View style={styles.infoGrid}>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Shipping Method
                                </Text>
                                <Text style={styles.infoValue}>
                                  {shippingMethodItem
                                    ? shippingMethodItem.value
                                    : ""}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Transport</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.transport}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Address</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.address}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Country</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.country || "-"}
                                </Text>
                              </View>
                            </View>
                          </View>
                        )}
                      </View>

                      {/* New Product Requirements Card */}
                      <View style={styles.card}>
                        <TouchableOpacity
                          style={styles.cardHeader}
                          onPress={() => toggleCard("vendor")}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.cardTitle}>
                            New Product Requirements
                          </Text>
                          <AntDesign
                            name={collapsedCards.vendor ? "down" : "up"}
                            size={20}
                            color="#495057"
                          />
                        </TouchableOpacity>
                        {!collapsedCards.vendor && (
                          <View style={styles.cardContent}>
                            <View style={styles.infoGrid}>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  NEW Line Item?
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.new_line_item_ || "-"}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Product Details
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.product_details || "-"}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Label Requirements
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.labelRequirements || "-"}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Artwork Approved
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.artworkApproved || "-"}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Artwork Ref. (Label 1)
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.artwork_ref___label_1_ || "-"}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Artwork Ref. (Label 2)
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.artwork_ref___label_2_ || "-"}
                                </Text>
                              </View>
                            </View>

                            {/* Product Flags */}
                            {selectedOrder?.new_product___artwork___sampling___label_flags && (
                              <View style={styles.vendorFlagsContainer}>
                                <Text style={styles.flagsLabel}>
                                  Product Requirements:
                                </Text>
                                <View style={styles.vendorFlagsWrapper}>
                                  {selectedOrder.new_product___artwork___sampling___label_flags
                                    .split(";")
                                    .filter((flag: string) => flag.trim())
                                    .map((flag: string, index: number) => {
                                      let flagStyle = styles.newItemFlag;
                                      if (
                                        flag.toLowerCase().includes("artwork")
                                      ) {
                                        flagStyle = styles.artworkFlag;
                                      } else if (
                                        flag.toLowerCase().includes("sampling")
                                      ) {
                                        flagStyle = styles.samplingFlag;
                                      }

                                      return (
                                        <View
                                          key={index}
                                          style={[styles.vendorFlag, flagStyle]}
                                        >
                                          <Text
                                            style={[
                                              styles.vendorFlagText,
                                              { color: flagStyle.color },
                                            ]}
                                          >
                                            {flag.trim()}
                                          </Text>
                                        </View>
                                      );
                                    })}
                                </View>
                              </View>
                            )}
                          </View>
                        )}
                      </View>

                      {/* Products Card */}
                      <View style={styles.card}>
                        <TouchableOpacity
                          style={styles.cardHeader}
                          onPress={() => toggleCard("products")}
                          activeOpacity={0.7}
                        >
                          <View>
                            <Text style={styles.cardTitle}>Products</Text>
                            <Text style={styles.sapNumber}>
                              SAP Doc Type: {selectedOrder?.sapDocType}
                            </Text>
                            <Text style={styles.sapNumber}>
                              SAP Doc #: {selectedOrder?.sapDocumentNumber}
                            </Text>
                          </View>
                          <AntDesign
                            name={collapsedCards.products ? "down" : "up"}
                            size={20}
                            color="#495057"
                          />
                        </TouchableOpacity>
                        {!collapsedCards.products && (
                          <View style={styles.cardContent}>
                            <View style={styles.locationInfo}>
                              <Text style={styles.infoLabel}>Location</Text>
                              <Text style={styles.infoValue}>
                                {order ? selectedOrder.location : ""}
                              </Text>
                            </View>

                            <View style={styles.tableContainer}>
                              <ScrollView
                                horizontal={mobileDevice ? true : false}
                                showsHorizontalScrollIndicator={false}
                              >
                                <DataTable>
                                  <DataTable.Header
                                    style={styles.datatableHeader}
                                  >
                                    <DataTable.Title
                                      style={styles.productColumn}
                                    >
                                      <Text
                                        style={styles.datatableHeaderItemText}
                                      >
                                        Product Code
                                      </Text>
                                    </DataTable.Title>
                                    <DataTable.Title
                                      style={styles.productColumn}
                                    >
                                      <Text
                                        style={styles.datatableHeaderItemText}
                                      >
                                        Product
                                      </Text>
                                    </DataTable.Title>
                                    <DataTable.Title
                                      numeric
                                      style={styles.orderQtyColumn}
                                    >
                                      <Text
                                        style={styles.datatableHeaderItemText}
                                      >
                                        Order Qty
                                      </Text>
                                    </DataTable.Title>
                                    <DataTable.Title style={styles.uom}>
                                      <Text
                                        style={styles.datatableHeaderItemText}
                                      >
                                        UoM
                                      </Text>
                                    </DataTable.Title>
                                    <DataTable.Title
                                      numeric
                                      style={styles.price}
                                    >
                                      {mobileDevice ? (
                                        <Text
                                          style={styles.datatableHeaderItemText}
                                        >
                                          Price
                                        </Text>
                                      ) : (
                                        <Text
                                          style={styles.datatableHeaderItemText}
                                        >
                                          Price{"\n"}(Ex GST)
                                        </Text>
                                      )}
                                    </DataTable.Title>
                                  </DataTable.Header>

                                  {selectedOrder.products &&
                                    selectedOrder.products?.length > 0 && (
                                      <>
                                        {selectedOrder.products.map(
                                          (p, index) => (
                                            <DataTable.Row
                                              key={index}
                                              style={styles.dataRow}
                                            >
                                              <View
                                                style={styles.productColumn}
                                              >
                                                <Text
                                                  style={styles.productCode}
                                                >
                                                  {p.ItemCode}
                                                </Text>
                                              </View>
                                              <View
                                                style={styles.productColumn}
                                              >
                                                <Text
                                                  style={styles.productName}
                                                >
                                                  {p.name}
                                                </Text>
                                                <Text style={styles.productId}>
                                                  {p.id}
                                                </Text>
                                              </View>
                                              <DataTable.Cell
                                                style={styles.orderQtyColumn}
                                                numeric
                                              >
                                                <Text style={styles.quantity}>
                                                  {p.quantity}
                                                </Text>
                                              </DataTable.Cell>
                                              <DataTable.Cell
                                                style={styles.uom}
                                              >
                                                <Text>{p.uom}</Text>
                                              </DataTable.Cell>
                                              <DataTable.Cell
                                                style={styles.price}
                                              >
                                                <Text style={styles.priceText}>
                                                  {selectedOrder?.orderType ===
                                                  "Contract Order"
                                                    ? "Contract"
                                                    : p.price}
                                                </Text>
                                              </DataTable.Cell>
                                            </DataTable.Row>
                                          )
                                        )}

                                        {/* Summary Rows (can stay outside scroll if you prefer) */}
                                        <View style={styles.summaryContainer}>
                                          <View style={styles.summaryRow}>
                                            <Text style={styles.summaryLabel}>
                                              Total Boxes:
                                            </Text>
                                            <Text style={styles.summaryValue}>
                                              {totalBoxes}
                                            </Text>
                                          </View>
                                          <View style={styles.summaryRow}>
                                            <Text style={styles.summaryLabel}>
                                              Total Pieces:
                                            </Text>
                                            <Text style={styles.summaryValue}>
                                              {totalPieces}
                                            </Text>
                                          </View>
                                          <View style={styles.summaryRow}>
                                            <Text style={styles.summaryLabel}>
                                              Total Amount:
                                            </Text>
                                            <Text style={styles.summaryValue}>
                                              {selectedOrder?.orderType ===
                                              "Contract Order"
                                                ? "Contract"
                                                : totalPrice}
                                            </Text>
                                          </View>
                                        </View>
                                      </>
                                    )}
                                </DataTable>
                              </ScrollView>
                            </View>
                          </View>
                        )}
                      </View>

                      {/* Files Card */}
                      <View style={styles.card}>
                        <TouchableOpacity
                          style={styles.cardHeader}
                          onPress={() => toggleCard("files")}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.cardTitle}>Uploaded Files</Text>
                          <AntDesign
                            name={collapsedCards.files ? "down" : "up"}
                            size={20}
                            color="#495057"
                          />
                        </TouchableOpacity>
                        {!collapsedCards.files && (
                          <View style={styles.cardContent}>
                            {(operationAttachmentsList &&
                              operationAttachmentsList.length > 0) ||
                            (selectedOrder?.attachments &&
                              selectedOrder.attachments.length > 0) ? (
                              <View style={styles.fileList}>
                                {[
                                  ...(operationAttachmentsList || []).map(
                                    (file) => ({
                                      fileName: file.name,
                                      fileUri: file.url,
                                      fileType: file.extension,
                                    })
                                  ),
                                  ...(selectedOrder?.attachments || []).map(
                                    (file) => ({
                                      fileName: file.fileName,
                                      fileUri: file.fileUri,
                                      fileType: file.fileType,
                                    })
                                  ),
                                ].map((file, index) => (
                                  <FilePreview
                                    key={index}
                                    fileName={file.fileName}
                                    fileUri={file.fileUri}
                                    fileType={file.fileType}
                                    styles={styles}
                                    mode="preview"
                                  />
                                ))}
                              </View>
                            ) : (
                              <Text style={styles.noFilesText}>
                                No files found
                              </Text>
                            )}
                          </View>
                        )}
                      </View>

                      {/* Freight Delivery Card */}
                      <View style={styles.card}>
                        <TouchableOpacity
                          style={styles.cardHeader}
                          onPress={() => toggleCard("freight")}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.cardTitle}>Freight Delivery</Text>
                          <AntDesign
                            name={collapsedCards.freight ? "down" : "up"}
                            size={20}
                            color="#495057"
                          />
                        </TouchableOpacity>
                        {!collapsedCards.freight && (
                          <View style={styles.cardContent}>
                            <View style={styles.freightContainer}>
                              <FontAwesome
                                name="dollar"
                                size={20}
                                color="#28a745"
                              />
                              <Text style={styles.freightValue}>
                                {selectedOrder.freightDelivery}
                              </Text>
                            </View>
                          </View>
                        )}
                      </View>

                      {/* Submission Details Card */}
                      <View style={[styles.card, styles.lastCard]}>
                        <TouchableOpacity
                          style={styles.cardHeader}
                          onPress={() => toggleCard("submission")}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.cardTitle}>
                            Submission Details
                          </Text>
                          <AntDesign
                            name={collapsedCards.submission ? "down" : "up"}
                            size={20}
                            color="#495057"
                          />
                        </TouchableOpacity>
                        {!collapsedCards.submission && (
                          <View style={styles.cardContent}>
                            <View style={styles.infoGrid}>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Processed By
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.createdByUser?.display_name ||
                                    "Not Set"}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Created At</Text>
                                <Text style={styles.infoValue}>
                                  {moment(selectedOrder.createdAt).format(
                                    "DD-MMM-YYYY HH:mm:ss"
                                  )}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Updated At</Text>
                                <Text style={styles.infoValue}>
                                  {moment(selectedOrder.updatedAt).format(
                                    "DD-MMM-YYYY HH:mm:ss"
                                  )}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>HubSpot ID</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder?.hubspotId}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>Remarks</Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.remarks}
                                </Text>
                              </View>
                              <View
                                style={[
                                  styles.infoItem,
                                  {
                                    width:
                                      isLargeScreen || isMediumDevice
                                        ? "48%"
                                        : "100%",
                                  },
                                ]}
                              >
                                <Text style={styles.infoLabel}>
                                  Internal Remarks
                                </Text>
                                <Text style={styles.infoValue}>
                                  {selectedOrder.internalRemarks || "-"}
                                </Text>
                              </View>
                            </View>
                          </View>
                        )}
                      </View>
                    </ScrollView>
                  </>
                ) : route.params.action === "requestHelp" ? (
                  <ScrollView style={styles.container}>
                    <DrawerHeader />
                    <OrderActionComponent
                      handleProcessOrder={handleProcessOrder}
                      sapDocNumber={sapDocNumber}
                      selectedOrder={selectedOrder}
                      setSapDocNumber={setSapDocNumber}
                    />
                  </ScrollView>
                ) : (
                  <UpdateConfirmationScreen
                    confirmationText={confirmationTextCS}
                    successfulText={successfulTextCS}
                    approve={approveCS}
                    reject={rejectCS}
                  />
                )}
              </>
            )}
          </View>
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#f8f9fa",
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    backgroundColor: "#ffffff",
    marginHorizontal: 8,
    marginVertical: 8,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  lastCard: {
    marginBottom: 16,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#212529",
  },
  sapNumber: {
    fontSize: 13,
    color: "#6c757d",
    marginTop: 2,
  },
  editButton: {
    padding: 6,
    borderRadius: 8,
    backgroundColor: "#f8f9fa",
  },
  cardContent: {
    padding: 16,
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: -12,
    marginHorizontal: -6, // Negative margin for gap between columns
  },
  infoItem: {
    width: "100%", // Default mobile: 1 column
    marginBottom: 12,
    paddingHorizontal: 6, // Gap between columns
    // On larger screens, this will be overridden inline to "48%" for 2 columns
  },
  fullWidth: {
    width: "100%",
    marginRight: 0,
  },
  infoLabel: {
    fontSize: 13,
    fontWeight: "500",
    color: "#6c757d",
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 15,
    color: "#212529",
    fontWeight: "400",
  },
  badge: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    overflow: "hidden",
    textAlign: "center",
  },
  statusBadge: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    overflow: "hidden",
    textAlign: "center",
  },
  flagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 12,
  },
  flag: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    marginBottom: 8,
  },
  urgentFlag: {
    backgroundColor: "#ffebee",
  },
  priceFlag: {
    backgroundColor: "#fff3e0",
  },
  approvedFlag: {
    backgroundColor: "#e8f5e8",
  },
  productFlag: {
    backgroundColor: "#f3e5f5",
  },
  flagText: {
    fontSize: 12,
    fontWeight: "500",
    color: "#d32f2f",
  },
  productFlagText: {
    color: "#7b1fa2",
  },
  vendorFlagsContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#e9ecef",
  },
  flagsLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#495057",
    marginBottom: 8,
  },
  vendorFlagsWrapper: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  vendorFlag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 8,
  },
  vendorFlagText: {
    fontSize: 12,
    fontWeight: "500",
  },
  newItemFlag: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
  },
  artworkFlag: {
    backgroundColor: "#fff3e0",
    color: "#f57c00",
  },
  samplingFlag: {
    backgroundColor: "#f3e5f5",
    color: "#7b1fa2",
  },
  locationInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    paddingHorizontal: 4,
  },
  tableContainer: {
    borderRadius: 8,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  datatableHeader: {
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 1,
    borderBottomColor: "#dee2e6",
  },
  datatableHeaderItemText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#495057",
    textAlign: "center",
  },
  productColumn: {
    flex: 2.5,
    paddingVertical: 12,
    paddingHorizontal: 8,
    justifyContent: "flex-start",
  },
  orderQtyColumn: {
    flex: 1,
    paddingVertical: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  uom: {
    flex: 1,
    paddingVertical: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  price: {
    flex: 1.2,
    paddingVertical: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  dataRow: {
    borderBottomWidth: 1,
    borderBottomColor: "#f8f9fa",
    minHeight: 60,
  },
  productCode: {
    fontSize: 14,
    fontWeight: "500",
    color: "#212529",
    marginBottom: 4,
  },
  productName: {
    fontSize: 14,
    color: "#212529",
    marginBottom: 2,
  },
  productId: {
    fontSize: 12,
    color: "#6c757d",
  },
  quantity: {
    fontSize: 14,
    fontWeight: "500",
    color: "#212529",
  },
  priceText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#28a745",
  },
  summaryContainer: {
    backgroundColor: "#f8f9fa",
    padding: 16,
    marginTop: 8,
    borderRadius: 8,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#495057",
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: "600",
    color: "#212529",
  },
  fileList: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  noFilesText: {
    textAlign: "center",
    color: "#6c757d",
    fontStyle: "italic",
    paddingVertical: 20,
  },
  freightContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f9fa",
    padding: 16,
    borderRadius: 8,
  },
  freightValue: {
    fontSize: 18,
    fontWeight: "600",
    color: "#28a745",
    marginLeft: 8,
  },
  drawerStyle: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#dee2e6",
    backgroundColor: "#fff",
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  headerBtnContainer: {
    padding: 5,
    marginHorizontal: 3,
  },
  imagePreview: {
    width: "100%",
    height: 80, // Reduced height for image preview
    backgroundColor: "#f5f5f5",
    borderTopWidth: 1,
    borderTopColor: "#eee",
  },
});

export default SingleOperation;
